import { FC } from 'react'
import InlineSvg from 'react-inlinesvg'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { PrismicRichText } from '@prismicio/react'
import { ImageField, LinkField } from '@prismicio/client'
import tw from 'twin.macro'
import { Body, Grid, LargeHeader, SubHeader } from '../UI'
import PrismicLinkInternalOrExternal from '../PrismicLinkInternalOrExternal'
import { RichTextFields } from '../../types/prismic'

import ctaArrowBlue from '../../images/icons/cta-arrow-blue.svg'
import { richTextStrongComponent } from '../../utils/richTextComponents'

type Props = {
  header: string
  subhead: RichTextFields
  items: Array<{
    title: string
    bullets: RichTextFields
    image: ImageField
    image_inset: ImageField
    image_tags: string
    image_quotes: string
    cta_text: string
    cta_link: LinkField
  }>
  cta_text: string
  cta_link: LinkField
  pagePath: string
  pageTitle: string
}

const HereForYou: FC<Props> = ({
  header,
  subhead,
  items,
  cta_text,
  cta_link,
  pagePath,
  pageTitle,
}) => {
  return (
    <section tw="pt-[40px] pb-[60px] lg:(pt-[80px] pb-[80px]) bg-gb-cream text-gb-blue font-domaine overflow-hidden relative">
      <Grid tw="mb-8">
        <div tw="col-span-full lg:(col-span-8)">
          <h2 css={[Body, tw`mb-[10px] lg:mb-[25px]`]}>{header}</h2>
          <h3 css={[LargeHeader, tw`mb-[18px]`]}>
            <PrismicRichText
              field={subhead.richText}
              components={richTextStrongComponent}
            />
          </h3>
        </div>
      </Grid>
      {items.map((item, index) => (
        <div
          key={index}
          tw="flex flex-col mb-8 lg:(flex-row mb-16 mx-[100px] gap-8)"
        >
          <div tw="relative w-full mb-8 lg:(mb-0 flex-1)">
            {!!item.image && (
              <GatsbyImage image={getImage(item.image)} alt="" />
            )}
            {!!item.image_inset && (
              <GatsbyImage
                image={getImage(item.image_inset)}
                alt=""
                tw="absolute bottom-[32px] right-[16px] lg:right-[-16px] w-3/4 rounded-md shadow"
              />
            )}
            {!!item.image_tags && (
              <div
                css={[
                  tw`absolute bottom-[16px] left-[16px] lg:left-[-16px] flex flex-col items-start gap-2`,
                  !!item.image_quotes && tw`hidden lg:flex`,
                ]}
              >
                {item.image_tags.split('//').map((tag, index) => (
                  <div
                    key={index}
                    tw="px-3 py-1 rounded-md bg-gb-magenta text-white"
                  >
                    {tag}
                  </div>
                ))}
              </div>
            )}
            {!!item.image_quotes && (
              <div tw="absolute bottom-[16px] max-lg:left-[16px] lg:(top-[16px] right-[-16px]) flex flex-col gap-2">
                {item.image_quotes.split('//').map((quote, index) => (
                  <div
                    key={index}
                    tw="px-2 py-1 rounded-[0.5rem 0.5rem 0.5rem 0] bg-white text-black text-[14px] shadow"
                  >
                    {quote}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div tw="flex flex-1 flex-col px-8">
            <p tw="mb-1">
              {index + 1 < 10 && '0'}
              {index + 1}
            </p>
            <h4 css={[SubHeader, tw`mb-2`]}>{item.title}</h4>
            <PrismicRichText
              field={item.bullets.richText}
              components={{
                list: ({ children }) => (
                  <ul tw="list-disc list-outside pl-4 mb-6">{children}</ul>
                ),
                oList: ({ children }) => (
                  <ol tw="list-disc list-outside pl-4 mb-6">{children}</ol>
                ),
                listItem: ({ children }) => <li>{children}</li>,
                oListItem: ({ children }) => <li>{children}</li>,
                strong: ({ children }) => (
                  <strong tw="uppercase font-object">{children}</strong>
                ),
              }}
            />
            {!!item.cta_text && !!item.cta_link && (
              <div tw="mb-4">
                <PrismicLinkInternalOrExternal
                  id="cta-learn-your-risk-2"
                  field={item.cta_link}
                  trackEventParams={{
                    pageTitle: pageTitle,
                    path: pagePath,
                    target: 'url' in item.cta_link ? item.cta_link.url : '',
                    copy: item.cta_text,
                  }}
                  cssStyle={tw`uppercase pt-2 pb-1 font-object text-[13px] line-height[13px] letter-spacing[1px] border-b border-gb-blue lg:(transition-colors hover:bg-gb-cream-dark)`}
                >
                  {item.cta_text}
                  <InlineSvg
                    src={ctaArrowBlue}
                    tw="w-6 ml-[5px] mt-[-2px] inline text-white"
                  />
                </PrismicLinkInternalOrExternal>
              </div>
            )}
          </div>
        </div>
      ))}
      {!!cta_text && !!cta_link && (
        <div tw="mx-[30px] lg:(flex justify-center)">
          <PrismicLinkInternalOrExternal
            id="cta-get-started"
            field={cta_link}
            trackEventParams={{
              pageTitle: pageTitle,
              path: pagePath,
              target: 'url' in cta_link ? cta_link.url : '',
              copy: cta_text,
            }}
            cssStyle={tw`border hover:bg-gb-cream-dark border-gb-blue uppercase px-6 py-4 col-span-full font-object text-[13px] line-height[13px] letter-spacing[1px] flex justify-center items-center sm:max-w-[350px] lg:(w-80 transition-colors)`}
          >
            {cta_text}
            <InlineSvg
              src={ctaArrowBlue}
              tw="w-6 ml-[5px] mt-[-2px] inline text-white"
            />
          </PrismicLinkInternalOrExternal>
        </div>
      )}
    </section>
  )
}

export default HereForYou
